/**
 * A Discovery row done by text or email (RM-399; Kapil's decision of 10/19,
 * the 10/2 availability pattern): RM writes the list with one token per
 * restaurant,
 *
 *   {{discovery-row:<base64url JSON>}}
 *
 * and the app draws each one as the Discovery card — the same AI-chat
 * restaurant row an app-channel Discovery turn shows (name, heart filled when
 * the member favorited it, the "Favorited by" photos of their network), with
 * the row's fact lines under it. This file reads a token;
 * lib/agent/discoveryRow.js on the server writes it (and turns it into text
 * lines for a text or an email). The two must agree field for field: the
 * server and client tests share one golden token.
 *
 * Every field is checked here too: a token that doesn't read is not drawn at
 * all — never shown as raw text.
 */

export const DISCOVERY_ROW_TOKEN_RE = /\{\{discovery-row:([A-Za-z0-9_-]{1,8000})\}\}/g

// A token still arriving while a reply streams: hidden until it is whole.
export const PARTIAL_DISCOVERY_ROW_RE = /\{\{discovery-row:[A-Za-z0-9_-]*\}?$/

const TOKEN_VERSION = 1
const LIMITS = { name: 200, line: 600, lines: 8, number: 999 }
const PROFILE_ID_RE = /^[A-Za-z0-9_-]{1,120}$/

function cleanLine(value, max = LIMITS.line) {
  return String(value == null ? '' : value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F\u2028\u2029]+/g, ' ')
    .replace(/[{}]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .slice(0, max)
}

const cleanLines = list => (Array.isArray(list)
  ? list.map(x => (typeof x === 'string' ? cleanLine(x) : '')).filter(Boolean).slice(0, LIMITS.lines)
  : [])

function base64UrlToUtf8(payload) {
  const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0))

  return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
}

/**
 * The checked row a decoded token carries, or null.
 * `lines` are the text's own (who favorited it — the card carries that as the
 * heart and the photos, so the app never prints them); `facts` show under the
 * card on every surface (what RM knows about group/private dining).
 */
export function normalizeDiscoveryRow(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  if (raw.v !== TOKEN_VERSION) return null
  const name = cleanLine(raw.n, LIMITS.name)
  if (!name) return null
  const profileId = typeof raw.p === 'string' && PROFILE_ID_RE.test(raw.p) ? raw.p : null
  const n = Number.isInteger(raw.i) && raw.i > 0 && raw.i <= LIMITS.number ? raw.i : null

  return {
    name,
    profileId,
    n,
    lines: cleanLines(raw.x),
    facts: cleanLines(raw.f),
    optedOut: raw.o === 1,
  }
}

/** A token's payload → its row, or null when it doesn't read. */
export function decodeDiscoveryRow(payload) {
  if (typeof payload !== 'string' || !/^[A-Za-z0-9_-]{1,8000}$/.test(payload)) return null
  try {
    return normalizeDiscoveryRow(JSON.parse(base64UrlToUtf8(payload)))
  } catch (err) {
    return null
  }
}

/** The row as plain lines, for "copy reply": what the member reads in the app (the name, the facts under it). */
export function discoveryRowPlainText(row) {
  if (!row) return ''

  return [row.n ? `${row.n}. ${row.name}` : row.name, ...row.facts].filter(Boolean).join('\n')
}
